import { hasPermission } from "@/lib/api";

/**
 * UI gates for actions whose endpoints are RBAC-protected, so a role never
 * sees a button that can only end in a 403 (e.g. a captain on /orders).
 * Backend RBAC stays the source of truth; these only mirror it.
 */
export interface POSActionPermissions {
  /** POST /orders/{id}/settle */
  canSettle: boolean;
  /** Manual discount, coupons, available-coupons list */
  canDiscount: boolean;
  /** UPI QR, POS terminal, loyalty redeem */
  canCollectPayment: boolean;
  /** Create / edit customer records */
  canManageCustomers: boolean;
  /** Table QR PIN, pending QR orders */
  canManageQrOrders: boolean;
}

export function posActionPermissions(): POSActionPermissions {
  return {
    canSettle: hasPermission("orders.settle"),
    canDiscount: hasPermission("orders.discount"),
    canCollectPayment: hasPermission("payments.collect"),
    canManageCustomers: hasPermission("customers.manage"),
    canManageQrOrders: hasPermission("qr_orders.manage"),
  };
}

export const canManageQrOrders = () => hasPermission("qr_orders.manage");
/**
 * The QR Accept prompt and the pending list it restores from: qr_orders.accept
 * (captains, cashiers and up) or qr_orders.manage. Reject and Modify stay with
 * qr_orders.manage.
 */
export const canAcceptQrOrders = () => hasPermission("qr_orders.accept") || hasPermission("qr_orders.manage");
/** KDS bump / ready / reopen (PATCH /kot/...) */
export const canUpdateKot = () => hasPermission("kot.update");
/** Completing an order (POST /orders/{id}/complete): orders.edit or orders.fulfill */
export const canFulfillOrders = () => hasPermission("orders.edit") || hasPermission("orders.fulfill");
/** Feedback analytics and other report endpoints */
export const canViewReports = () => hasPermission("reports.view");
/** Reservations: confirm / check in / no-show / cancel; seating and freeing tables */
export const canManageTables = () => hasPermission("tables.manage");
/**
 * Editing the floor plan: add (single and bulk), edit, hide/activate and delete
 * tables, and floor areas. Manager, admin and owner only; captains and cashiers
 * run service with tables.manage but never see these controls.
 */
export const canEditTables = () => hasPermission("tables.configure");
/** Audit trails (e.g. reservation audit) */
export const canViewAudit = () => hasPermission("audit.view");
/** Open the cash drawer for a shift (POST /outlets/{id}/till/open) */
export const canOpenTill = () => hasPermission("till.open");
/** Close the till on one's own authority; till.open alone closes with a manager PIN */
export const canCloseTill = () => hasPermission("till.close");
/** Till status chip / settle reminder: anyone who opens or closes the till */
export const canUseTill = () => hasPermission("till.open") || hasPermission("till.close");
